import { CustomPicture, CustomSource } from "~/components/ui/CustomPicture"
import type { ImageWidget } from "~/types/widgets"

import Image from "~/components/ui/Image"
import BrandContactForm from "~/components/brandCustomPage/contact/BrandContactForm"

interface LogoContainerProps {
  title: string
  colorTitle: string
  logo: ImageWidget | undefined
  altLogo: string
}

function LogoContainer({ title, colorTitle, logo, altLogo }: LogoContainerProps) {
  return (
    <div className='flex items-center gap-5 sm:gap-10 justify-between lg:justify-start w-full lg:w-fit'>
      <span
        className='text-xl lg:text-2xl 2xl:text-3xl font-bold max-w-[calc(100%-112px-20px)] sm:max-w-[calc(100%-112px-40px)] lg:max-w-[226px] 2xl:max-w-[282px]'
        style={{ color: colorTitle }}
      >
        {title}
      </span>

      {!!logo && (
        <Image
          src={logo}
          className='w-full h-full max-w-28 lg:max-w-48 xl:max-w-60 object-cover'
          width={240}
          height={140}
          alt={altLogo}
        />
      )}
    </div>
  )
}

export interface Props {
  /**
   * @title Nome da marca da página. Exemplo: My Vib OU La Pimienta
   * @description Esse nome será usado para organização da tabela no MasterData
   */
  brandName: string

  /**
   * @title Imagem de fundo - Desktop
   */
  imageDesktop: ImageWidget

  /**
   * @title Imagem de fundo - Mobile
   */
  imageMobile: ImageWidget

  /**
   * @title Texto Alternativo da imagem de fundo
   */
  altImage: string

  /**
   * @title Título
   * @description Se não for passado um título, será renderizado o default ("Se inscreva para começar a revender")
   */
  title?: string

  /**
   * @title Cor do texto do título
   * @format color
   */
  colorTitle?: string

  /**
   * @title Logo da marca
   * @description Preferencialmente usar formato SVG
   */
  logo?: ImageWidget

  /**
   * @title Texto Alternativo do logo da marca
   */
  altLogo?: string

  /**
   * @title Texto do botão de enviar formulário
   * @description Se não for passado um texto para o botão, será renderizado o default ("Inscrever-se")
   */
  submitButton?: string

  /**
   * @title Mensagem de sucesso
   * @description Se não for passado um texto de mensagem, será renderizado o default ("Você receberá um e-mail com o nosso passo a passo para revender os produtos em sua loja.")
   */
  successMessage?: string
}

function BrandContact(
  {
    brandName,
    imageDesktop,
    imageMobile,
    altImage,
    title,
    colorTitle,
    logo,
    altLogo,
    submitButton,
    successMessage,
  }: Awaited<ReturnType<typeof loader>>,
) {
  return (
    <div id='form-resell' className='w-full max-w-[1834px] mx-auto px-4 mt-12'>
      <div className='w-full min-h-[474px] lg:min-h-96 rounded-xl overflow-hidden flex items-center justify-center relative gap-10'>
        <CustomPicture className='h-full w-full absolute'>
          <CustomSource
            media='(max-width: 1023px)'
            src={imageMobile}
            width={768}
            height={474}
            fetchPriority='low'
            factors={[0.5, 1]}
          />
          <CustomSource
            media='(min-width: 1024px)'
            src={imageDesktop}
            width={1802}
            height={384}
            fetchPriority='low'
            factors={[0.5, 0.75, 1]}
          />
          <img
            loading='lazy'
            src={imageDesktop}
            alt={altImage}
            className='h-full w-full object-cover'
          />
        </CustomPicture>

        <div className='w-full h-full min-h-[474px] lg:min-h-96 flex flex-col lg:flex-row items-center justify-between z-[1] py-7 px-5 lg:px-7 2xl:px-36 gap-7 lg:gap-10'>
          <LogoContainer title={title} colorTitle={colorTitle} logo={logo} altLogo={altLogo} />
          <BrandContactForm brand={brandName} submit={submitButton} message={successMessage} />
        </div>
      </div>
    </div>
  )
}

export const loader = (props: Props) => {
  const title = props.title ?? 'Se inscreva para começar a revender'
  const colorTitle = props.colorTitle ?? '#333333'
  const altLogo = props.altLogo ?? 'Logo da marca'
  const submitButton = props.submitButton ?? 'Inscrever-se'
  const successMessage = props.successMessage ??
    'Você receberá um e-mail com o nosso passo a passo para revender os produtos em sua loja.'

  return { ...props, title, colorTitle, altLogo, submitButton, successMessage }
}

export default BrandContact
